import {useQuery} from 'react-query'

import {API} from '../../networking/http'

// Rain check (D450). The server owns every amount and every decision: the client only prints what
// `GET bookings/:id/rain-check` says and never computes or sends an amount. There is no write route:
// the card's button runs the ordinary cancel flow (`PUT bookings/:id/cancel`).
//
// Every field is optional/nullable on purpose. A server that has not shipped rain check answers 404
// (or something unexpected) and the page then behaves exactly as it did before this feature.

export type RainCheckState = 'claimed' | 'external' | 'free' | 'none' | 'open'

export type RainCheckPolicy = {
  name?: string | null
  customerText?: string | null
  shareReason?: string | null
  refundPercent?: number | null
}

export type RainCheckClaim = {
  // 'refunded' | 'cancelled' | 'needs_a_look'; any other word is read as "cancelled, nothing more known".
  status?: string | null
  refunded?: number | null
  paid?: number | null
  currency?: string | null
  claimedAt?: number | string | null
}

export type RainCheckOffer = {
  state: RainCheckState
  reason?: string | null
  // Epoch milliseconds (an ISO string is also read).
  endsAt?: number | string | null
  policy?: RainCheckPolicy | null
  booking?: {
    id?: string | null
    name?: string | null
    startsAt?: number | string | null
    reference?: string | null
  } | null
  paid?: number | null
  currency?: string | null
  refundAmount?: number | null
  // state external: 'paid_at_venue' or 'several_payments' (why the system cannot refund it). Missing (older server) reads as paid_at_venue.
  externalReason?: string | null
  // state free: true when the session goes back to the pass. Missing reads as false.
  returnsPassSession?: boolean | null
  claim?: RainCheckClaim | null
}

export const RAIN_CHECK_QUERY_KEY = 'getBookingRainCheckQuery'

export const makeGetBookingRainCheckEndpoint = (id: string) => `bookings/${id}/rain-check`

const SHOWN_STATES: RainCheckState[] = ['open', 'free', 'external', 'claimed']

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

// Only a state we know how to say honestly is shown. `none`, an unknown (future) state, a malformed
// body, an `open` offer with no refund amount (the card would promise money the server did not name)
// and a `claimed` answer with no claim all read as "no card".
export const parseRainCheckOffer = (data: unknown): RainCheckOffer | null => {
  if (!isRecord(data)) {
    return null
  }
  const state = data.state as RainCheckState
  if (!SHOWN_STATES.includes(state)) {
    return null
  }
  const offer = data as unknown as RainCheckOffer
  if (state === 'open' && !(typeof offer.refundAmount === 'number' && Number.isFinite(offer.refundAmount))) {
    return null
  }
  if (state === 'claimed' && !isRecord(offer.claim)) {
    return null
  }
  return offer
}

// Fail safe: any failure (404 on a server without rain check, 401, 5xx, network) is "no card". The
// ordinary Cancel is still on the page and nothing is shown, logged to the user, or retried.
export const fetchBookingRainCheck = (id: string): Promise<RainCheckOffer | null> =>
  API.get<undefined, unknown>(makeGetBookingRainCheckEndpoint(id))
    .then((res) => parseRainCheckOffer(res.data))
    .catch(() => null)

export const getBookingRainCheckQuery = (id?: string | null, enabled = true) => ({
  queryKey: [RAIN_CHECK_QUERY_KEY, id ?? null],
  queryFn: () => fetchBookingRainCheck(id ?? ''),
  enabled: Boolean(id) && enabled,
  retry: false,
  cacheTime: 0,
})

export const useBookingRainCheck = (id?: string | null, enabled = true) =>
  useQuery(getBookingRainCheckQuery(id, enabled))
